import React, { useState, useEffect } from 'react';
import { CheckCircle2, Clock, AlertCircle, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { useToast } from './ui/use-toast';

interface UserTask {
  userTaskId: number;
  taskId: number;
  title: string;
  description: string | null;
  taskType: 'aviso' | 'validacion';
  moduleTarget: string | null;
  status: 'pendiente' | 'completada';
  completedAt: string | null;
  assignedAt: string;
}

interface TaskWidgetProps {
  showCompleted?: boolean;
  maxItems?: number;
}

const TaskWidget: React.FC<TaskWidgetProps> = ({ showCompleted = false, maxItems = 5 }) => {
  const [tasks, setTasks] = useState<UserTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchMyTasks();
  }, [showCompleted]);

  const fetchMyTasks = async () => {
    try {
      setLoading(true);
      const url = showCompleted
        ? '/api/tasks/my/tasks'
        : '/api/tasks/my/tasks?status=pendiente';
      
      const response = await fetch(url, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Error al cargar tareas');
      }

      const data = await response.json();
      setTasks(data.tasks || []);
    } catch (error) {
      console.error('Error fetching my tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async (userTaskId: number, taskType: string) => {
    if (taskType === 'validacion') {
      toast({
        title: 'Información',
        description: 'Esta tarea se completa automáticamente cuando realices la acción requerida',
      });
      return;
    }

    try {
      const response = await fetch(`/api/tasks/my/tasks/${userTaskId}/complete`, {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Error al completar tarea');
      }

      toast({
        title: 'Éxito',
        description: 'Tarea completada correctamente',
      });

      fetchMyTasks();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Error al completar tarea',
        variant: 'destructive',
      });
    }
  };

  const getModuleDescription = (moduleTarget: string | null) => {
    const moduleMap: Record<string, string> = {
      'alumno/documentos': 'Sube tus documentos personales',
      'alumno/perfil': 'Completa tu perfil',
    };
    return moduleTarget ? moduleMap[moduleTarget] || moduleTarget : null;
  };

  const displayTasks = expanded ? tasks : tasks.slice(0, maxItems);
  const pendingCount = tasks.filter((t) => t.status === 'pendiente').length;
  const completedCount = tasks.filter((t) => t.status === 'completada').length;

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Cargando tareas...</span>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Mis Tareas</h3>
          <p className="text-sm text-gray-600">
            {pendingCount} pendiente{pendingCount !== 1 ? 's' : ''} · {completedCount} completada{completedCount !== 1 ? 's' : ''}
          </p>
        </div>
        {tasks.length > 0 && (
          <div className="flex items-center gap-2">
            {pendingCount > 0 && (
              <span className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                <Clock className="w-3 h-3" />
                {pendingCount}
              </span>
            )}
          </div>
        )}
      </div>

      {tasks.length === 0 ? (
        <div className="text-center py-8">
          <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <p className="text-gray-600">No tienes tareas asignadas</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {displayTasks.map((task) => (
              <div
                key={task.userTaskId}
                className={`p-4 border rounded-lg ${
                  task.status === 'completada' ? 'bg-gray-50 border-gray-200' : 'bg-white border-gray-300'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {task.status === 'completada' ? (
                        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                      ) : (
                        <Clock className="w-4 h-4 text-orange-600 flex-shrink-0" />
                      )}
                      <h4 className={`font-medium ${task.status === 'completada' ? 'text-gray-600 line-through' : 'text-gray-900'}`}>
                        {task.title}
                      </h4>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          task.taskType === 'aviso'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-purple-100 text-purple-700'
                        }`}
                      >
                        {task.taskType === 'aviso' ? 'Manual' : 'Automática'}
                      </span>
                    </div>

                    {task.description && (
                      <p className="text-sm text-gray-600 mb-2">{task.description}</p>
                    )}

                    {task.taskType === 'validacion' && task.moduleTarget && (
                      <p className="text-xs text-purple-600 font-medium">
                        {getModuleDescription(task.moduleTarget)}
                      </p>
                    )}

                    {task.status === 'completada' && task.completedAt && (
                      <p className="text-xs text-gray-500 mt-2">
                        Completada el {new Date(task.completedAt).toLocaleDateString('es-MX')}
                      </p>
                    )}
                  </div>

                  {task.status === 'pendiente' && task.taskType === 'aviso' && (
                    <Button
                      size="sm"
                      onClick={() => handleCompleteTask(task.userTaskId, task.taskType)}
                      className="flex-shrink-0"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Completar
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {tasks.length > maxItems && (
            <Button
              variant="outline"
              className="w-full mt-4"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? 'Ver menos' : `Ver todas (${tasks.length})`}
              <ChevronRight className={`w-4 h-4 ml-2 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            </Button>
          )}
        </>
      )}
    </Card>
  );
};

export default TaskWidget;
